import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import moment from 'moment';

import { apiPatch, apiPost } from '@src/services/api/helpers';
import {
    normalizeRecipientsForPayload,
    REPORT_PERIOD_OPTIONS,
} from '@src/utils/scheduling';
import RecipientsInput from '@src/custom/RecipientsInput/RecipientsInput';
import ScheduleDateRangeInput from './ScheduleDateRangeInput';
import ScheduleFrequencyInput from './ScheduleFrequencyInput';
import SchedulePeriodInput from './SchedulePeriodInput';

const defaultTitle = (isEdit) => (isEdit ? 'Edit Schedule' : 'Schedule');

const ScheduleDialog = ({
    open,
    schedule,
    onClose,
    onSuccess,
    notify,
    endpoint = '/schedules/announcement_reports',
    scheduleType = 'Schedule::AnnouncementReport',
    title = defaultTitle,
    periodOptions = REPORT_PERIOD_OPTIONS,
    customFields = null,
}) => {
    const isEdit = Boolean(schedule?.id);

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [frequency, setFrequency] = useState('monthly');
    const [frequencyDay, setFrequencyDay] = useState('1st');
    const [timeOfDay, setTimeOfDay] = useState('08:00');
    const [reportPeriod, setReportPeriod] = useState('previous_month');
    const [recipients, setRecipients] = useState([]);
    const [recipientsError, setRecipientsError] = useState('');
    const [startsOn, setStartsOn] = useState(moment().format('YYYY-MM-DD'));
    const [endsOn, setEndsOn] = useState('');
    const [emailCreatorOnFailure, setEmailCreatorOnFailure] = useState(true);
    const [loading, setLoading] = useState(false);

    const notifyUser = (message, optionsOrType = 'info') => {
        if (!notify) return;
        const options = typeof optionsOrType === 'string'
            ? { type: optionsOrType }
            : optionsOrType;

        notify(message, options);
    };

    useEffect(() => {
        if (!open) return;

        setNameError('');
        setRecipientsError('');

        if (schedule) {
            setName(schedule.name || '');
            setFrequency(schedule.frequency || 'monthly');
            setFrequencyDay(schedule.frequency_day || '1st');
            setTimeOfDay(schedule.time_of_day || '08:00');
            setReportPeriod(schedule.period_type || schedule.report_period || (periodOptions?.[0]?.value || 'previous_month'));
            setRecipients(Array.isArray(schedule.recipients) ? schedule.recipients : []);
            setStartsOn(schedule.starts_on ? moment(schedule.starts_on).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'));
            setEndsOn(schedule.ends_on ? moment(schedule.ends_on).format('YYYY-MM-DD') : '');
            setEmailCreatorOnFailure(schedule.email_creator_on_failure !== false);
        } else {
            setName('');
            setFrequency('monthly');
            setFrequencyDay('1st');
            setTimeOfDay('08:00');
            setReportPeriod(periodOptions?.[0]?.value || 'previous_month');
            setRecipients([]);
            setStartsOn(moment().format('YYYY-MM-DD'));
            setEndsOn('');
            setEmailCreatorOnFailure(true);
        }
    }, [open, schedule, periodOptions]);

    const validateForm = () => {
        if (!name.trim()) {
            setNameError('Please enter a schedule name');
            notifyUser('Please enter a schedule name', 'warning');
            return false;
        }

        setNameError('');

        if (!recipients || recipients.length === 0) {
            setRecipientsError('Please specify at least one recipient');
            notifyUser('Please specify at least one recipient', 'warning');
            return false;
        }

        setRecipientsError('');

        if (!startsOn || !moment(startsOn).isValid()) {
            notifyUser('Please choose a valid start date', 'warning');
            return false;
        }

        if (endsOn && (!moment(endsOn).isValid() || moment(startsOn).isAfter(moment(endsOn)))) {
            notifyUser('Starts on date cannot be after Ends on date', 'warning');
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        const uniqueRecipients = normalizeRecipientsForPayload(recipients);

        const payload = {
            name: name.trim(),
            type: scheduleType,
            frequency,
            frequency_day: frequencyDay,
            time_of_day: timeOfDay,
            recipients: uniqueRecipients,
            starts_on: startsOn,
            ends_on: endsOn || null,
            email_creator_on_failure: emailCreatorOnFailure,
        };

        if (periodOptions && periodOptions.length > 0) {
            payload.period_type = reportPeriod;
        }

        setLoading(true);
        try {
            if (isEdit) {
                await apiPatch(`${endpoint}/${schedule.id}`, payload);
                notifyUser('Schedule updated successfully', 'info');
            } else {
                await apiPost(endpoint, payload);
                notifyUser('Schedule created successfully', 'info');
            }

            onSuccess();
            onClose();
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
            notifyUser(errorMsg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const dialogTitle = typeof title === 'function'
        ? title(isEdit)
        : (title || (isEdit ? 'Edit Schedule' : 'Schedule'));

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={() => !loading && onClose()}
        >
            <DialogTitle sx={{ fontWeight: 600 }}>
                {dialogTitle}
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
                <TextField
                    fullWidth
                    label="Name"
                    value={name}
                    disabled={loading}
                    error={Boolean(nameError)}
                    helperText={nameError || 'Shown in the Schedules list and as the email subject.'}
                    placeholder="e.g. Monthly report - Marketing"
                    onChange={(e) => {
                        setName(e.target.value);
                        if (e.target.value.trim()) {
                            setNameError('');
                        }
                    }}
                />

                <ScheduleFrequencyInput
                    frequency={frequency}
                    frequencyDay={frequencyDay}
                    timeOfDay={timeOfDay}
                    disabled={loading}
                    onFrequencyChange={setFrequency}
                    onFrequencyDayChange={setFrequencyDay}
                    onTimeOfDayChange={setTimeOfDay}
                />

                {periodOptions && periodOptions.length > 0 ? (
                    <SchedulePeriodInput
                        reportPeriod={reportPeriod}
                        periodOptions={periodOptions}
                        frequency={frequency}
                        frequencyDay={frequencyDay}
                        timeOfDay={timeOfDay}
                        startsOn={startsOn}
                        disabled={loading}
                        onReportPeriodChange={setReportPeriod}
                    />
                ) : null}

                <RecipientsInput
                    value={recipients}
                    disabled={loading}
                    error={Boolean(recipientsError)}
                    helperText={recipientsError || 'Pick admin users or type any email address. Each recipient gets the download link when the report is ready.'}
                    onChange={(newValue) => {
                        setRecipients(newValue);
                        if (newValue && newValue.length > 0) {
                            setRecipientsError('');
                        }
                    }}
                />

                <ScheduleDateRangeInput
                    startsOn={startsOn}
                    endsOn={endsOn}
                    disabled={loading}
                    onStartsOnChange={setStartsOn}
                    onEndsOnChange={setEndsOn}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={emailCreatorOnFailure}
                            disabled={loading}
                            color="primary"
                            onChange={(e) => setEmailCreatorOnFailure(e.target.checked)}
                        />
                    }
                    label="Email the schedule creator if a run fails"
                />

                {typeof customFields === 'function'
                    ? customFields({
                        formState: {
                            name,
                            frequency,
                            frequencyDay,
                            timeOfDay,
                            reportPeriod,
                            recipients,
                            startsOn,
                            endsOn,
                            emailCreatorOnFailure,
                        },
                        schedule,
                        isEdit,
                        loading,
                    })
                    : customFields}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    disabled={loading}
                    onClick={onClose}
                >
                    CANCEL
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={handleSave}
                >
                    SAVE SCHEDULE
                    {loading ? <CircularProgress size={16} sx={{ ml: 1 }} /> : null}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ScheduleDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    schedule: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    notify: PropTypes.func.isRequired,
    endpoint: PropTypes.string,
    scheduleType: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    periodOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        }),
    ),
    customFields: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

ScheduleDialog.defaultProps = {
    schedule: null,
    endpoint: '/schedules/announcement_reports',
    scheduleType: 'Schedule::AnnouncementReport',
    title: defaultTitle,
    periodOptions: REPORT_PERIOD_OPTIONS,
    customFields: null,
};

export default ScheduleDialog;
